import { useCallback, useEffect, useRef, useState } from 'react'
import { parseStoredBoolean, WAKE_LOCK_STORAGE_KEY } from '../lib/playbackPreferences'

function loadInitialEnabled(): boolean {
  try {
    return parseStoredBoolean(localStorage.getItem(WAKE_LOCK_STORAGE_KEY), true)
  } catch {
    return true
  }
}

/**
 * Screen Wake Lock을 재생 중 자동으로 요청하되 사용자가 직접 끌 수 있게 합니다.
 * 선택은 브라우저에 저장되어 다음 연습 화면에서도 유지됩니다.
 */
export function useWakeLock() {
  const [active, setActive] = useState(false)
  const [enabled, setEnabledState] = useState(loadInitialEnabled)
  const enabledRef = useRef(enabled)
  const sentinel = useRef<WakeLockSentinel | null>(null)

  const storeEnabled = useCallback((next: boolean) => {
    enabledRef.current = next
    setEnabledState(next)
    try {
      localStorage.setItem(WAKE_LOCK_STORAGE_KEY, String(next))
    } catch {
      // 저장소를 쓸 수 없어도 현재 화면의 선택은 유지합니다.
    }
  }, [])

  const release = useCallback(async () => {
    if (sentinel.current && !sentinel.current.released) {
      await sentinel.current.release()
    }
    sentinel.current = null
    setActive(false)
  }, [])

  const requestNow = useCallback(async () => {
    if (!('wakeLock' in navigator)) return false
    if (sentinel.current && !sentinel.current.released) {
      setActive(true)
      return true
    }

    const value = await navigator.wakeLock.request('screen')
    sentinel.current = value
    setActive(true)
    value.addEventListener('release', () => {
      if (sentinel.current === value) sentinel.current = null
      setActive(false)
    }, { once: true })
    return true
  }, [])

  /** 재생 시작에서 호출합니다. 사용자가 OFF로 정한 경우에는 다시 켜지 않습니다. */
  const request = useCallback(async () => {
    if (!enabledRef.current) return false
    return requestNow()
  }, [requestNow])

  const enable = useCallback(async () => {
    storeEnabled(true)
    return requestNow()
  }, [requestNow, storeEnabled])

  const disable = useCallback(async () => {
    storeEnabled(false)
    await release()
    return true
  }, [release, storeEnabled])

  useEffect(() => () => {
    void release()
  }, [release])

  return {
    active,
    disable,
    enable,
    enabled,
    release,
    request,
    supported: 'wakeLock' in navigator,
  }
}
